/* =========================================================
   CLINIQUE NOUR – MOBILMENU CLEANUP
   Fjerner søgefeltet og den tomme mørkeblå rest.
   Land, valuta og sprog bevares.
========================================================= */

(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';

  const DRAWER_SELECTOR = [
    '.menu-drawer',
    '.header-menu-drawer',
    '.drawer-menu',
    '.mobile-menu-drawer',
    '.mobile-navigation-drawer',
    'header-drawer',
    'menu-drawer',
    '[data-menu-drawer]',
    '[data-mobile-menu]'
  ].join(',');

  const SEARCH_SELECTOR = [
    '.cn-menu-search-wrap',
    '.cn-menu-search',
    '.search-drawer-content',
    '.search-page-content',
    '.header-search-form',
    '.drawer-search',
    '.drawer-search-wrapper',
    '.menu-drawer-search',
    '.menu-drawer__search',
    '.mobile-menu-search',
    '.mobile-menu__search',
    'predictive-search',
    'search-form',
    'form[role="search"]',
    'form[action*="/search"]',
    'input[type="search"]',
    'input[name="q"]'
  ].join(',');

  const SEARCH_ROOT_SELECTOR = [
    '.cn-menu-search-wrap',
    '.search-drawer-content',
    '.search-page-content',
    '.header-search-form',
    '.drawer-search',
    '.drawer-search-wrapper',
    '.menu-drawer-search',
    '.menu-drawer__search',
    '.mobile-menu-search',
    '.mobile-menu__search',
    'predictive-search',
    'search-form',
    'form[role="search"]',
    'form[action*="/search"]'
  ].join(',');

  /*
    Disse elementer må aldrig fjernes.
    De styrer land, valuta og sprog.
  */
  const LOCALIZATION_SELECTOR = [
    'localization-form',
    '.localization-form',
    '[data-localization-form]',
    '[class*="localization" i]',
    'form[action*="/localization"]',
    'select[name="country_code"]',
    'select[name="language_code"]',
    'input[name="country_code"]',
    'input[name="language_code"]'
  ].join(',');

  const isMobile = () => {
    return window.matchMedia(MOBILE_QUERY).matches;
  };

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number.parseFloat(style.opacity || '1') > 0 &&
      rect.width > 1 &&
      rect.height > 1
    );
  };

  const getOpenDrawer = () => {
    if (!isMobile()) {
      return null;
    }

    const candidates = Array.from(
      document.querySelectorAll(DRAWER_SELECTOR)
    ).filter(isVisible);

    return (
      candidates.find((drawer) => {
        const rect = drawer.getBoundingClientRect();

        const explicitlyOpen =
          drawer.matches(
            '.open, .active, .is-open, [open], [aria-hidden="false"]'
          ) ||
          drawer.closest(
            '.open, .active, .is-open, [open], [aria-hidden="false"]'
          );

        const looksLikeDrawer =
          rect.left < 80 &&
          rect.width >= 220 &&
          rect.height >= window.innerHeight * 0.55;

        return Boolean(
          explicitlyOpen || looksLikeDrawer
        );
      }) || null
    );
  };

  const hasLocalization = (element) => {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    return (
      element.matches(LOCALIZATION_SELECTOR) ||
      Boolean(
        element.querySelector(LOCALIZATION_SELECTOR)
      )
    );
  };

  const horizontalOverlap = (
    firstRect,
    secondRect
  ) => {
    return Math.max(
      0,
      Math.min(firstRect.right, secondRect.right) -
        Math.max(firstRect.left, secondRect.left)
    );
  };

  const belongsToDrawer = (
    element,
    drawer
  ) => {
    if (drawer.contains(element)) {
      return true;
    }

    if (!isVisible(element)) {
      return false;
    }

    const elementRect =
      element.getBoundingClientRect();

    const drawerRect =
      drawer.getBoundingClientRect();

    const overlap = horizontalOverlap(
      elementRect,
      drawerRect
    );

    return (
      overlap >=
        Math.min(
          elementRect.width,
          drawerRect.width
        ) *
          0.55 &&
      elementRect.top >=
        drawerRect.top +
          drawerRect.height * 0.5 &&
      elementRect.top <= drawerRect.bottom + 40
    );
  };

  const findSearchRoot = (element) => {
    if (!(element instanceof HTMLElement)) {
      return null;
    }

    return (
      element.closest(SEARCH_ROOT_SELECTOR) ||
      element.closest('form') ||
      element
    );
  };

  const removeSearchElements = (drawer) => {
    document
      .querySelectorAll(SEARCH_SELECTOR)
      .forEach((element) => {
        if (!(element instanceof HTMLElement)) {
          return;
        }

        if (!belongsToDrawer(element, drawer)) {
          return;
        }

        const root = findSearchRoot(element);

        if (!root || hasLocalization(root)) {
          return;
        }

        root.remove();
      });
  };

  const parseRgb = (value) => {
    const match = String(value).match(
      /rgba?\(\s*(\d+)\D+(\d+)\D+(\d+)(?:\D+([\d.]+))?/
    );

    if (!match) {
      return null;
    }

    return {
      red: Number(match[1]),
      green: Number(match[2]),
      blue: Number(match[3]),
      alpha:
        match[4] === undefined
          ? 1
          : Number(match[4])
    };
  };

  const hasDarkBackground = (element) => {
    const style =
      window.getComputedStyle(element);

    const color = parseRgb(
      style.backgroundColor
    );

    if (!color || color.alpha < 0.15) {
      return false;
    }

    const brightness =
      color.red * 0.299 +
      color.green * 0.587 +
      color.blue * 0.114;

    return brightness < 105;
  };

  const hasUsefulControls = (element) => {
    if (hasLocalization(element)) {
      return true;
    }

    return Boolean(
      element.querySelector(
        [
          'select',
          'a[href]:not([href*="/search"])',
          'button:not([type="submit"])',
          'input:not([type="search"]):not([name="q"])'
        ].join(',')
      )
    );
  };

  /*
    Fjerner den tomme mørkeblå container,
    som står tilbage efter søgefeltet.
  */
  const removeEmptyDarkStrip = (drawer) => {
    const drawerRect =
      drawer.getBoundingClientRect();

    document
      .querySelectorAll('body *')
      .forEach((element) => {
        if (!(element instanceof HTMLElement)) {
          return;
        }

        if (
          element === drawer ||
          !isVisible(element) ||
          hasLocalization(element)
        ) {
          return;
        }

        const rect =
          element.getBoundingClientRect();

        const overlap = horizontalOverlap(
          rect,
          drawerRect
        );

        const text = element.textContent
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();

        const nearDrawerBottom =
          rect.top >=
            drawerRect.top +
              drawerRect.height * 0.72 &&
          rect.top <= drawerRect.bottom + 35;

        const wideEnough =
          rect.width >= drawerRect.width * 0.5 &&
          overlap >= rect.width * 0.55;

        const compactHeight =
          rect.height >= 6 &&
          rect.height <= 150;

        const onlySearchText =
          text === '' ||
          text === 'søg' ||
          text === 'søg...' ||
          text.startsWith('søg efter') ||
          text === 'search' ||
          text === 'search...';

        if (
          nearDrawerBottom &&
          wideEnough &&
          compactHeight &&
          onlySearchText &&
          hasDarkBackground(element) &&
          !hasUsefulControls(element)
        ) {
          element.remove();
        }
      });
  };

  const cleanDrawer = () => {
    const drawer = getOpenDrawer();

    if (!drawer) {
      return;
    }

    removeSearchElements(drawer);
    removeEmptyDarkStrip(drawer);
  };

  let cleanupScheduled = false;

  const scheduleCleanup = () => {
    if (cleanupScheduled) {
      return;
    }

    cleanupScheduled = true;

    window.requestAnimationFrame(() => {
      cleanupScheduled = false;
      cleanDrawer();
    });
  };

  const initialize = () => {
    cleanDrawer();

    const observer = new MutationObserver(
      scheduleCleanup
    );

    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'class',
          'open',
          'aria-hidden',
          'style'
        ]
      }
    );

    document.addEventListener(
      'click',
      () => {
        window.setTimeout(
          scheduleCleanup,
          40
        );

        window.setTimeout(
          scheduleCleanup,
          180
        );

        window.setTimeout(
          scheduleCleanup,
          500
        );
      },
      true
    );

    document.addEventListener(
      'shopify:section:load',
      scheduleCleanup
    );

    document.addEventListener(
      'shopify:section:select',
      scheduleCleanup
    );

    window.addEventListener(
      'resize',
      scheduleCleanup
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();